import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Building2, Search, PackageOpen, Upload, LogOut, X, Trophy, Award, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { TangramIcon } from "@/components/TangramIcon";
import { NotificationsBell } from "@/components/NotificationsBell";
import { hasPermission } from "@/lib/permissions";
import { Settings2, Users2, FolderCog } from "lucide-react";

const navItems = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/equip-office", label: "Equipamiento provisional", icon: Building2, testid: "nav-equip", perm: "equip_site_office" },
  { to: "/search", label: "Buscar equipamiento", icon: Search, testid: "nav-search" },
  { to: "/surplus", label: "Material sobrante", icon: PackageOpen, testid: "nav-surplus" },
  { to: "/upload", label: "Subir activo", icon: Upload, testid: "nav-upload" },
  { to: "/employees", label: "People", icon: Users, testid: "nav-employees" },
  { to: "/leaderboard", label: "Ranking de proyectos", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/me", label: "Mi reconocimiento", icon: Award, testid: "nav-recognition" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const roleLabel = {
    admin: "Administrador",
    project_manager: "Gerente de Proyecto",
    cost_dept: "Costos",
  }[user?.role] || user?.role;

  const isLobby = loc.pathname === "/";

  return (
    <div className={`min-h-screen flex ${isLobby ? "bg-[#FAF7F2]" : "bg-[#FAFAFA]"}`}>
      {/* Sidebar (overlay-only on Lobby) */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#E2E8F0] transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} ${isLobby ? "" : "lg:static lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center px-6 border-b border-[#E2E8F0]">
          <Link to="/" className="flex items-center gap-2 font-display text-lg font-extrabold text-[#0A2540] tracking-tight" data-testid="logo">
            <TangramIcon size={20} outline stroke="#0A2540" /> TANGRAM
          </Link>
        </div>
        <nav className="px-3 py-5 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid, perm }) => {
            if (perm && !hasPermission(user, perm)) return null;
            return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0A2540] text-white"
                    : "text-[#09101D] hover:bg-[#EAF0F6]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {label}
            </NavLink>
            );
          })}
          {hasPermission(user, "manage_assets") && (
            <NavLink
              to="/admin/inventario"
              data-testid="nav-admin-inventory"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 mt-2 rounded-sm text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0A2540] text-white" : "text-[#09101D] hover:bg-[#EAF0F6]"
                }`
              }
            >
              <Settings2 size={16} strokeWidth={1.75} />
              Administración
            </NavLink>
          )}
          {hasPermission(user, "manage_staff") && (
            <NavLink
              to="/admin/staff"
              data-testid="nav-admin-staff"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0A2540] text-white" : "text-[#09101D] hover:bg-[#EAF0F6]"
                }`
              }
            >
              <Users2 size={16} strokeWidth={1.75} />
              Control de staff
            </NavLink>
          )}
          {hasPermission(user, "manage_projects") && (
            <NavLink
              to="/admin/proyectos"
              data-testid="nav-admin-projects"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0A2540] text-white" : "text-[#09101D] hover:bg-[#EAF0F6]"
                }`
              }
            >
              <FolderCog size={16} strokeWidth={1.75} />
              Control de proyectos
            </NavLink>
          )}
          {user?.role === "app_admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-alta-empresa"
              onClick={() => setOpen(false)}
              className="block mt-4 px-3 py-1.5 text-[11px] text-[#94A3B8] hover:text-[#5C6A7D] tracking-wide transition-colors"
            >
              dar de alta empresa
            </NavLink>
          )}
        </nav>
      </aside>

      {/* Backdrop */}
      {open && <div className={`fixed inset-0 z-30 bg-black/40 ${isLobby ? "" : "lg:hidden"}`} onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {isLobby ? (
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <button
              onClick={() => setOpen(true)}
              data-testid="lobby-menu-toggle"
              aria-label="Abrir menú principal"
              className="p-2.5 rounded-full bg-white shadow-sm border border-[#E2E8F0] text-[#0A2540] hover:bg-[#F5F1EA] transition-colors"
            >
              <TangramIcon size={18} />
            </button>
            <div className="flex items-center gap-2">
              <NotificationsBell variant="lobby" />
              <button
                onClick={logout}
                data-testid="lobby-logout"
                aria-label="Cerrar sesión"
                className="p-2.5 rounded-full bg-white shadow-sm border border-[#E2E8F0] text-[#0A2540] hover:bg-[#F5F1EA] transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        ) : (
          <header className="h-16 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0] sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-sm border border-[#E2E8F0]"
                onClick={() => setOpen(v => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Abrir menú principal"
              >
                {open ? <X size={18} /> : <TangramIcon size={18} />}
              </button>
              <div className="text-xs uppercase tracking-[0.2em] text-[#5C6A7D] hidden sm:block">
                Gestión de Activos de Construcción
              </div>
            </div>
            <div className="flex items-center gap-4">
              <NotificationsBell variant="header" />
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-[#0A2540]" data-testid="user-name">{user?.display_name || user?.alias || user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6A7D]" data-testid="user-role">{roleLabel}</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-button"
                className="text-[#0A2540] hover:bg-[#EAF0F6] rounded-sm"
              >
                <LogOut size={16} strokeWidth={1.75} className="mr-1.5" />
                Cerrar sesión
              </Button>
            </div>
          </header>
        )}
        <main className={isLobby ? "flex-1 flex flex-col w-full" : "flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-7xl w-full mx-auto"}>
          {children}
        </main>
      </div>
    </div>
  );
}
